import React, { useCallback, useEffect, useRef, useState } from 'react';
import WorkspaceSidebar from './WorkspaceSidebar';
import RightPanel from './RightPanel';
import { storageImage, onStorageImageError } from '../../lib/img';

const BellIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const SparkleIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 2l1.8 5.6L19 9l-5.2 1.4L12 16l-1.8-5.6L5 9l5.2-1.4L12 2z" />
  </svg>
);

// A typed line reads as a question for epio (route to chat) rather than a
// search (route to the command palette) when it ends with "?" or opens with
// a question/help word. Keeps ONE bar instead of two separate affordances —
// the person doesn't have to know which to use.
const QUESTION_STARTERS = /^(what|how|why|who|when|which|should|can|could|will|help|invite|ask|write|plan|pitch|suggest)\b/i;
function looksLikeQuestion(text) {
  const t = (text || '').trim();
  if (!t) return false;
  return t.endsWith('?') || QUESTION_STARTERS.test(t);
}

/**
 * Global top bar — one combined "Ask epio or search" field, notifications
 * bell, avatar. Sits above the center stage on every page (desktop only;
 * mobile has its own top bar with a sparkle chat shortcut instead — no room
 * for a full bar there, and the bottom nav already covers navigation).
 * Distinct from the per-page WorkspaceTopBar that some views render inside
 * their own content column for a title/actions row.
 */
function WorkspaceGlobalTopBar({
  user, profile, onOpenSearch, onOpenNotifications, onNavigate, onAskEpio, notificationCount = 0,
}) {
  const [value, setValue] = useState('');
  if (!user) return null;
  const initials = (profile?.full_name?.[0] || user?.email?.[0] || '?').toUpperCase();

  function submit() {
    const t = value.trim();
    if (!t) { onOpenSearch?.(); return; }
    if (looksLikeQuestion(t)) { onAskEpio?.(t); } else { onOpenSearch?.(t); }
    setValue('');
  }

  return (
    <div className="ws-global-topbar">
      <div className="ws-global-search">
        <SearchIcon />
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
          onFocus={() => { if (!value) onOpenSearch?.(); }}
          placeholder="Ask epio or search guests, topics, brands…"
        />
      </div>
      <div className="ws-global-topbar-actions">
        <button
          type="button"
          className="ws-global-bell"
          onClick={() => onOpenNotifications?.()}
          aria-label="Notifications"
          title="Notifications"
        >
          <BellIcon />
          {notificationCount > 0 && <span className="ws-global-bell-dot" aria-hidden />}
        </button>
        <button
          type="button"
          className="ws-global-avatar"
          onClick={() => onNavigate?.('profile')}
          aria-label="Your profile"
          title={profile?.full_name || user?.email || 'Profile'}
        >
          {profile?.avatar_url
            ? <img src={storageImage(profile.avatar_url, { width: 72 })} onError={(e) => onStorageImageError(e, profile.avatar_url)} alt="" />
            : <span>{initials}</span>}
        </button>
      </div>
    </div>
  );
}

const MIN_RIGHT_WIDTH = 240;
const MAX_RIGHT_WIDTH = 1200;
// First-paint default: 50% of the viewport. Intentionally NOT persisted —
// the panel resets to 50% on every page load (the user can drag it around
// within a session, but each fresh load starts back at 50%). Clamped to
// MIN/MAX.
function defaultRightWidth() {
  if (typeof window === 'undefined') return 640;
  return Math.min(MAX_RIGHT_WIDTH, Math.max(MIN_RIGHT_WIDTH, Math.round(window.innerWidth * 0.5)));
}

/**
 * Three-zone Suno-inspired workspace shell.
 *
 *   ┌──────────┬─────────────────────────┬─────────┐
 *   │ Sidebar  │ Center stage (children) │ Right   │
 *   │ 240px    │ flexible width          │ 260-560 │
 *   │          │                         │  drag   │
 *   └──────────┴─────────────────────────┴─────────┘
 *
 * The right panel is resizable via the thin bar on its left edge. Width is
 * persisted in localStorage so it stays put between sessions.
 *
 * Right panel hides below 1100px; sidebar collapses to icons below 768px.
 */
export default function WorkspaceLayout({
  children,
  user, profile,
  view, onNavigate, onSignOut, onOpenAuth,
  shows, activeShowId, onSelectShow, onManageShows,
  canCreateShow, onCreateShow,
  onOpenSearch, onOpenNotifications, notificationCount, inboxCount = 0, onAskEpio,
  rightPanel,
  rightPanelTitle,
  rightPanelOpen = true,
  onToggleRightPanel,
}) {
  const [localRightOpen, setLocalRightOpen] = useState(rightPanelOpen);
  const rightOpen = onToggleRightPanel ? rightPanelOpen : localRightOpen;
  const toggleRight = onToggleRightPanel || (() => setLocalRightOpen(v => !v));

  // Mobile nav: the bottom tab bar is gone — a hamburger in the mobile top
  // bar slides the sidebar in as a drawer. Closes on navigate + on view change.
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => { setDrawerOpen(false); }, [view]);
  const handleNavigate = useCallback((v) => { setDrawerOpen(false); onNavigate?.(v); }, [onNavigate]);

  // Sidebar collapse state — owned by the sidebar but mirrored here so the
  // grid template can shrink the first column to 64px. The rail stays
  // expanded on EVERY page (including create / internal) and only narrows
  // when the user manually collapses it (persisted). Previously non-home
  // views force-collapsed here, which left the menu closed on create even
  // though the sidebar rendered expanded.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { if (window.matchMedia('(max-width: 768px)').matches) return false; } catch {}
    try { return localStorage.getItem('sidebar-collapsed') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    function onCollapse(e) { setSidebarCollapsed(!!e.detail); }
    window.addEventListener('ws:sidebar-collapse', onCollapse);
    return () => window.removeEventListener('ws:sidebar-collapse', onCollapse);
  }, []);
  const sidebarWidth = sidebarCollapsed ? 64 : 240;

  // Right-panel width (px). Session-only: starts at 50% on every load, the
  // user can drag it during the session, and it's NOT persisted — a fresh
  // load resets to 50%.
  const [rightWidth, setRightWidth] = useState(defaultRightWidth);

  const dragStateRef = useRef(null);
  const shellRef = useRef(null);

  // Drag-to-resize the right panel. Works on mouse + touch. We listen on
  // window so dragging past the divider doesn't lose the pointer.
  const onDividerPointerDown = useCallback((e) => {
    e.preventDefault();
    dragStateRef.current = {
      startX: e.clientX ?? e.touches?.[0]?.clientX ?? 0,
      startWidth: rightWidth,
    };
    document.body.classList.add('ws-resizing');
  }, [rightWidth]);

  useEffect(() => {
    function move(e) {
      if (!dragStateRef.current) return;
      const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
      const delta = dragStateRef.current.startX - x; // dragging LEFT grows the right panel
      let next = dragStateRef.current.startWidth + delta;
      next = Math.min(MAX_RIGHT_WIDTH, Math.max(MIN_RIGHT_WIDTH, next));
      setRightWidth(next);
    }
    function up() {
      if (!dragStateRef.current) return;
      dragStateRef.current = null;
      document.body.classList.remove('ws-resizing');
      // Session-only: not persisted, so the panel resets to 50% next load.
    }
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
    };
  }, []);

  // If the view doesn't have a right panel to show, don't reserve any grid
  // column for it. This keeps the Shows / Library / Search / Profile pages
  // nice and wide.
  const hasRightPanel = rightPanel !== undefined && rightPanel !== null;
  // Home's right rail ("Opportunities for you" / "Recent activity") is a
  // fixed-width static panel per the Home Redesign — no drag-to-resize
  // handle, no collapse chevron. The Workshop/Document panels elsewhere
  // keep the resizable behavior.
  const isHomeView = view === 'shows' || view === 'dashboard';
  const HOME_RIGHT_WIDTH = 360;
  const rightOpenEffective = isHomeView ? true : rightOpen;
  const gridTemplate = !hasRightPanel
    ? `${sidebarWidth}px 1fr`
    : isHomeView
      ? `${sidebarWidth}px 1fr ${HOME_RIGHT_WIDTH}px`
      : rightOpen
        ? `${sidebarWidth}px 1fr 6px ${rightWidth}px`
        : `${sidebarWidth}px 1fr 44px`;

  return (
    <>
    <div
      ref={shellRef}
      className={`ws-shell${rightOpen && hasRightPanel ? ' ws-shell-resizable' : ''}${hasRightPanel && !rightOpen ? ' ws-shell-right-collapsed' : ''}${drawerOpen ? ' ws-shell--drawer-open' : ''}`}
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {/* Mobile top bar — hidden on desktop via CSS. Hamburger opens the
          sidebar as a slide-in drawer; the wordmark taps home. On the Clips
          feed it goes transparent (feed modifier) so it floats over video. */}
      <header className={`ws-mobile-topbar${view === 'clips' ? ' ws-mobile-topbar--feed' : ''}`}>
        <button
          type="button"
          className="ws-hamburger"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          aria-haspopup="menu"
          aria-expanded={drawerOpen}
        >
          <span /><span /><span />
        </button>
        <button
          type="button"
          className="ws-mobile-topbar-brand"
          onClick={() => handleNavigate('dashboard')}
          aria-label="Home"
        >
          epio<span className="ws-brand-dot">.</span>
        </button>
        {/* No room for the full "Ask epio or search" bar on mobile, and the
            bottom nav already covers navigation — a sparkle shortcut opens
            Create with the chat ready instead. */}
        {user && (
          <button
            type="button"
            className="ws-mobile-ask-epio"
            onClick={() => onAskEpio ? onAskEpio('') : handleNavigate('builder')}
            aria-label="Ask epio"
            title="Ask epio"
          >
            <SparkleIcon size={17} />
          </button>
        )}
      </header>

      {/* Backdrop behind the drawer — tap to close (mobile only). */}
      <div className="ws-drawer-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden />

      <WorkspaceSidebar
        user={user}
        profile={profile}
        view={view}
        onNavigate={handleNavigate}
        onSignOut={onSignOut}
        onOpenAuth={onOpenAuth}
        shows={shows}
        activeShowId={activeShowId}
        onSelectShow={onSelectShow}
        onManageShows={onManageShows}
        canCreateShow={canCreateShow}
        onCreateShow={onCreateShow}
        onOpenNotifications={onOpenNotifications}
        notificationCount={notificationCount}
        notifCount={notificationCount}
        inboxCount={inboxCount}
      />
      <div className="ws-content-col">
        <WorkspaceGlobalTopBar
          user={user}
          profile={profile}
          onOpenSearch={onOpenSearch}
          onOpenNotifications={onOpenNotifications}
          onNavigate={handleNavigate}
          onAskEpio={onAskEpio}
          notificationCount={notificationCount}
        />
        <main className="ws-stage">
          {children}
        </main>
      </div>
      {hasRightPanel && !isHomeView && rightOpen && (
        <div
          className="ws-divider"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize workspace panel"
          onMouseDown={onDividerPointerDown}
          onTouchStart={onDividerPointerDown}
          title="Drag to resize"
        />
      )}
      {hasRightPanel && (
        <RightPanel
          open={rightOpenEffective}
          onToggle={isHomeView ? undefined : toggleRight}
          title={rightPanelTitle}
          fixed={isHomeView}
        >
          {rightPanel}
        </RightPanel>
      )}
    </div>
  </>
  );
}
